import { ComponentType, ReactNode, SVGProps } from "react";

import classNames from "../../utils/classNames";

interface RowCardProps {
  // Small label that anchors the card — identical treatment for every card
  // in the row so the cells feel like one cohesive strip.
  title: string;
  // Optional icon rendered next to the title. Defaults to nothing so callers
  // can pass any 24/outline or 24/solid heroicon.
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  // Optional element rendered at the top-right of the card header. Use this
  // for status pills, headline badges, etc.
  aside?: ReactNode;
  // Ring colour override so cards like WilmaIntelligence can still reflect a
  // dynamic status while keeping every other card-chrome concern identical.
  ringClassName?: string;
  // Allow callers to stop propagation / override specific behaviours.
  onClick?: (e: React.MouseEvent) => void;
  // Extra class names to layer on the card root (keep rare — padding and
  // chrome should stay uniform).
  className?: string;
  // Body content.
  children?: ReactNode;
}

// Shared card shell used by every cell in the Awaiting Inventory table so the
// rows read as a unified strip rather than a collection of disparate panels.
// The padding, border, ring, gradient, and header strip are all locked down
// here — resist the urge to drift these per-cell.
export default function RowCard({
  title,
  icon: Icon,
  aside,
  ringClassName = "ring-slate-200",
  onClick,
  className,
  children,
}: RowCardProps) {
  return (
    <div
      onClick={onClick}
      className={classNames(
        // h-full + flex column lets the card stretch to the tallest card in
        // the row (paired with an `h-px` td so percentage height resolves).
        "flex h-full w-full min-w-0 flex-col rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50/80 to-white p-3 shadow-sm ring-1 ring-inset",
        ringClassName,
        className
      )}
    >
      <div className="flex min-h-[1.25rem] items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {Icon && (
            <Icon className="h-3.5 w-3.5 text-primary-500" aria-hidden="true" />
          )}
          <span>{title}</span>
        </div>
        {aside}
      </div>

      <div className="mt-2 flex min-w-0 flex-1 flex-col">{children}</div>
    </div>
  );
}
